import classnames from "classnames";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import RHFController from "../../Form/Form/RHFController";
import Icon from "../../Icon";
import { FormControl } from "../Form";
import FormHelper from "../FormHelper";
import FormInput from "../FormInput";
import Label from "../Label";
import CustomSelectInput from "./CustomSelectInput";

import "./selectInput.scss";

const SelectInput = ({
  name,
  label,
  options,
  helperText,
  className,
  typeahead,
  onChange,
  confirmOptions,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [userInput, setUserInput] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [selectOptions, setSelectOptions] = useState(options?.items || []);
  const {
    register,
    control,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
  } = useFormContext();
  const currentValue = useWatch({
    control,
    name,
  });
  const hasValue = typeof currentValue !== "undefined" && currentValue !== "";
  const currentError = errors[name];

  const handleOnActive = (isActive) => {
    setIsActive(isActive);
  };

  useEffect(() => {
    setSelectOptions(options.items || []);
  }, [options]);

  return (
    <FormControl
      className={classnames({
        "is-hidden": options?.hidden,
      })}
    >
      <FormInput
        type="select"
        className={classnames(className, {
          "form-input--typeahead": typeahead,
          "has-value": hasValue,
          "has-error": currentError,
          "is-expanded": showOptions,
          "is-active": userInput || isActive,
          "is-disabled": options?.disabled,
        })}
      >
        <Label
          htmlFor={`${name}-button`}
          text={label}
          required={options?.required}
          showRequiredAsterisk={options?.required}
        />

        <RHFController
          {...{
            register,
            name,
            value: currentValue,
            rules: { options },
            onChange,
            render: (props) => (
              <CustomSelectInput
                {...props}
                required={options?.required}
                showOptions={showOptions}
                handleShowOption={setShowOptions}
                handleUserInput={setUserInput}
                handleOnActive={handleOnActive}
                items={selectOptions}
                label={label}
                setError={setError}
                trigger={trigger}
                clearErrors={clearErrors}
                disabled={options?.disabled}
                readOnly={options?.readOnly}
                typeahead={typeahead}
                confirmOptions={confirmOptions}
              />
            ),
          }}
        />

        {(currentError || hasValue) && (
          <div className="form-validation-icons">
            {hasValue && <Icon name="system-checkmark" theme="success" />}
            {currentError && <Icon name="system-warning" theme="error" />}
          </div>
        )}
      </FormInput>

      <FormHelper helperText={helperText} error={currentError} label={label} />
    </FormControl>
  );
};

SelectInput.propTypes = {
  options: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  helperText: PropTypes.string,
  typeahead: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  confirmOptions: PropTypes.object,
};

SelectInput.defaultProps = {
  options: {},
  onChange: () => {},
};

export default SelectInput;
